<!-- ── Confirmed state ── -->
@if (confirmed) {
  <div class="booking-confirmed">
    <i class="pi pi-check-circle" aria-hidden="true"></i>
    <div>
      <strong>{{ confirmedLabel }}</strong>
      @if (confirmedTime) {
        <p>{{ confirmedTimeLabel }}</p>
      }
    </div>
  </div>
} @else {

  <div class="booking-calendar">

    <!-- Header -->
    <div class="booking-calendar__header">
      <i class="pi pi-calendar" aria-hidden="true"></i>
      <span>{{ headerVerb }} {{ execName }}</span>
      <span class="booking-calendar__duration">{{ getMeetingMinutes() }} min</span>
    </div>

    <!-- Range pill -->
    <div class="booking-range-pill">
      <span class="booking-range-pill__label">
        <i class="pi pi-calendar-clock booking-range-pill__icon" aria-hidden="true"></i>
        {{ rangeLabel }}
      </span>
      <div class="booking-range-pill__actions">
        <button type="button" class="booking-range-pill__btn"
          [class.booking-range-pill__btn--active]="rangeInputOpen()"
          [disabled]="disabled || slotPending()"
          (click)="onToggleRangeInput()"
          [attr.aria-expanded]="rangeInputOpen()">
          <i class="pi pi-sliders-h" aria-hidden="true"></i>
          {{ hasActiveRange ? 'Edit' : 'Filter dates' }}
        </button>
        @if (hasActiveRange) {
          <button type="button" class="booking-range-pill__btn booking-range-pill__btn--reset"
            [disabled]="disabled || slotPending()"
            (click)="onResetRange()" aria-label="Reset to all days">
            <i class="pi pi-times" aria-hidden="true"></i> Reset
          </button>
        }
      </div>
    </div>

    <!-- Range editor -->
    @if (rangeInputOpen()) {
      <fieldset class="booking-range-editor">
        <legend class="sr-only">Pick a date range</legend>
        <div class="booking-range-editor__chips">
          <button type="button" class="booking-range-editor__chip"
            [class.booking-range-editor__chip--active]="activeQuickChip() === 'today'"
            (click)="onQuickPick('today')">Today</button>
          <button type="button" class="booking-range-editor__chip"
            [class.booking-range-editor__chip--active]="activeQuickChip() === 'this-week'"
            (click)="onQuickPick('this-week')">This week</button>
          <button type="button" class="booking-range-editor__chip"
            [class.booking-range-editor__chip--active]="activeQuickChip() === 'next-week'"
            (click)="onQuickPick('next-week')">Next week</button>
          <button type="button" class="booking-range-editor__chip"
            [class.booking-range-editor__chip--active]="activeQuickChip() === 'all'"
            (click)="onQuickPick('all')">All days</button>
        </div>
        <div class="booking-range-editor__inputs">
          <label class="booking-range-editor__field">
            <span>From</span>
            <input type="date"
              [value]="rangeInputFrom()"
              [min]="minDateKey" [max]="maxDateKey"
              (input)="onRangeInputChange('from', $any($event.target).value)" />
          </label>
          <label class="booking-range-editor__field">
            <span>To</span>
            <input type="date"
              [value]="rangeInputTo()"
              [min]="minDateKey" [max]="maxDateKey"
              (input)="onRangeInputChange('to', $any($event.target).value)" />
          </label>
          <button type="button" class="booking-range-editor__apply"
            [disabled]="!rangeInputFrom() || !rangeInputTo()"
            (click)="onApplyRange()">Apply</button>
        </div>
      </fieldset>
    }

    <!-- Date strip with left/right paging arrows -->
    <div class="date-strip-nav">
      <button type="button" class="date-strip-nav__arrow"
        [disabled]="!canPageLeft || disabled || slotPending()"
        (click)="onPageLeft()" aria-label="Show earlier dates">
        <i class="pi pi-chevron-left" aria-hidden="true"></i>
      </button>

      <fieldset class="date-strip">
        <legend class="sr-only">Select a date</legend>
        @for (day of pagedDays; track day.dateKey) {
          <button type="button" class="date-strip__day"
            [class.date-strip__day--active]="selectedDateKey() === day.dateKey"
            [class.date-strip__day--today]="day.isToday"
            [class.date-strip__day--no-slots]="!day.hasSlots"
            [disabled]="!day.hasSlots || disabled || slotPending()"
            (click)="onDateSelect(day.dateKey)"
            [attr.aria-label]="day.dayName + ' ' + day.dayNum + ' ' + day.month"
            [attr.aria-pressed]="selectedDateKey() === day.dateKey">
            <span class="date-strip__day-name">{{ day.dayName }}</span>
            <span class="date-strip__day-num">{{ day.dayNum }}</span>
            <span class="date-strip__month">{{ day.month }}</span>
          </button>
        }
      </fieldset>

      <button type="button" class="date-strip-nav__arrow"
        [disabled]="!canPageRight || disabled || slotPending()"
        (click)="onPageRight()" aria-label="Show later dates">
        <i class="pi pi-chevron-right" aria-hidden="true"></i>
      </button>
    </div>

    <!-- Time slot grid -->
    <div class="booking-calendar__time-header">
      <strong>What time works?</strong>
      <div class="booking-calendar__tz-row">
        <span class="booking-calendar__tz-hint">
          {{ getMeetingMinutes() }} min meeting
        </span>
        <label class="booking-calendar__tz-label">Time zone</label>
        <app-timezone-select
          [value]="displayTimezone()"
          [disabled]="disabled || lockTimezone"
          [compact]="true"
          (valueChange)="onTimezoneChange($event)">
        </app-timezone-select>
      </div>
    </div>

    <fieldset class="time-slots">
      <legend class="sr-only">Available time slots</legend>
      @if (selectedDateKey()) {
        @let daySlots = getSlotsForDate(selectedDateKey()!);
        @for (slot of daySlots; track slot.startTime) {
          <button type="button" class="time-slot-tile"
            [class.time-slot-tile--pending]="slotPending()"
            [disabled]="disabled || slotPending() || isConfirming"
            (click)="onSlotSelect(slot)"
            [attr.aria-label]="slotActionVerb + ' ' + formatTime(slot.startTime)">
            @if (isConfirming && slotPending()) {
              <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
            } @else {
              {{ formatTime(slot.startTime) }}
            }
          </button>
        }
        @if (daySlots.length === 0) {
          <p class="time-slots__empty">No available times on this day.</p>
        }
      } @else if (hasActiveRange && visibleDays.length === 0) {
        <!-- Only show "no times in range" when the filter genuinely excludes all days -->
        <div class="time-slots__empty-range">
          <p>No available times in this range.</p>
          <button type="button" class="booking-range-editor__chip" (click)="onWidenRange()">
            Reset to all days
          </button>
        </div>
      } @else {
        <p class="time-slots__prompt">← Select a date above</p>
      }
    </fieldset>

  </div>

}
